import { useState, useMemo, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Flag, CheckCircle2, Circle, DollarSign, Clock } from "lucide-react";

const RATE_TYPES = [
  { key: "student_rate", label: "Student" },
  { key: "wet_rate", label: "Wet (incl. fuel)" },
  { key: "dry_rate", label: "Dry (excl. fuel)" },
];

export default function ProgressLedger({ program, summary, records, rateOptions, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const logged = summary?.hours_logged || 0;

  // ── Hour minimums (admin-editable) ──
  const [mins, setMins] = useState(() => (program.hour_minimums || []).map((m) => ({ ...m })));
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const totalMin = mins.find((m) => /total/i.test(m.label || ""));

  const saveMins = async () => {
    setSaving(true);
    try {
      await api.put(`/training/programs/${program.id}/minimums`, {
        hour_minimums: mins.filter((m) => (m.label || "").trim()).map((m) => ({ ...m, hours: +m.hours || 0 })),
      });
      toast.success("Minimums saved"); setEditing(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  // ── Milestones ──
  const milestones = useMemo(
    () => (program.lessons || []).filter((l) => l.milestone).sort((a, b) => a.seq - b.seq),
    [program.lessons]
  );

  // ── Cost estimate ──
  const DISCIPLINE_TYPE = { drone: "Drone", helicopter: "Helicopter", aeroplane: "Aeroplane" };
  const wantType = DISCIPLINE_TYPE[program.discipline];
  const allAircraft = rateOptions?.aircraft || [];
  const aircraft = wantType ? allAircraft.filter((a) => a.asset_type === wantType) : allAircraft;
  const instructors = rateOptions?.instructors || [];
  const [acId, setAcId] = useState("");
  const [rateType, setRateType] = useState("student_rate");
  const [instrId, setInstrId] = useState("none");
  useEffect(() => {
    if (aircraft.length && !aircraft.some((a) => a.id === acId)) setAcId(aircraft[0].id);
    if (!aircraft.length && acId) setAcId("");
  }, [aircraft, acId]);
  const ac = aircraft.find((a) => a.id === acId);
  const instr = instructors.find((i) => i.id === instrId);
  const acRate = ac?.[rateType] || 0;
  const instrRate = instr?.hire_rate || 0;
  const hourly = acRate + instrRate;
  const estimate = logged * hourly;
  const remainToTotal = totalMin ? Math.max(0, (totalMin.hours || 0) - logged) : 0;
  const projected = totalMin ? (totalMin.hours || 0) * hourly : 0;

  return (
    <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid={`ledger-${program.id}`}>
      <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-1.5">
        <Clock className="h-4 w-4" /> Progress ledger
      </h3>

      {/* Milestones */}
      {milestones.length > 0 && (
        <div className="mb-4">
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Milestones</Label>
          <div className="mt-2 flex flex-wrap gap-2" data-testid={`milestones-${program.id}`}>
            {milestones.map((l) => {
              const done = records[l.id]?.status === "competent";
              return (
                <Badge key={l.id} className={`border gap-1 ${done ? "bg-lime-100 text-lime-700 border-lime-200" : "bg-white text-slate-500 border-slate-200"}`} data-testid={`milestone-${l.id}`}>
                  {done ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
                  <Flag className="h-3 w-3" /> {l.title}
                </Badge>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {/* Hour minimums */}
        <div className="rounded-lg bg-white border border-slate-200 p-3">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Syllabus hour minimums</Label>
            {isAdmin && !editing && <Button size="sm" variant="ghost" className="text-xs h-7 text-slate-500" onClick={() => setEditing(true)} data-testid={`edit-mins-${program.id}`}>Edit</Button>}
          </div>

          {totalMin && (
            <div className="mb-3">
              <div className="flex justify-between text-xs mb-1"><span className="text-slate-500">Logged {logged}h of {totalMin.hours}h</span><span className="font-semibold text-slate-800">{remainToTotal}h to go</span></div>
              <Progress value={totalMin.hours ? Math.min(100, (logged / totalMin.hours) * 100) : 0} className="h-2 bg-slate-100" />
            </div>
          )}

          {!editing ? (
            mins.length === 0 ? (
              <p className="text-xs text-slate-400">No minimums set{isAdmin ? " — click Edit to add CASA hour targets." : "."}</p>
            ) : (
              <ul className="space-y-1.5" data-testid={`mins-list-${program.id}`}>
                {mins.map((m) => (
                  <li key={m.id} className="flex justify-between text-sm">
                    <span className="text-slate-600">{m.label}</span>
                    <span className="font-semibold text-slate-800">{m.hours}h</span>
                  </li>
                ))}
              </ul>
            )
          ) : (
            <div className="space-y-2">
              {mins.map((m, i) => (
                <div key={m.id || i} className="flex items-center gap-2">
                  <Input value={m.label} placeholder="Label (e.g. Total aeronautical)" className="h-8 text-sm" data-testid={`min-label-${i}`}
                    onChange={(e) => setMins((x) => x.map((y, j) => j === i ? { ...y, label: e.target.value } : y))} />
                  <Input type="number" step="0.1" value={m.hours} className="h-8 w-20 text-sm" data-testid={`min-hours-${i}`}
                    onChange={(e) => setMins((x) => x.map((y, j) => j === i ? { ...y, hours: e.target.value } : y))} />
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-500 shrink-0" onClick={() => setMins((x) => x.filter((_, j) => j !== i))} data-testid={`min-del-${i}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
              <Button size="sm" variant="outline" className="text-xs h-8 w-full" onClick={() => setMins((x) => [...x, { id: `new-${Date.now()}`, label: "", hours: 0 }])} data-testid={`min-add-${program.id}`}><Plus className="h-3.5 w-3.5 mr-1" /> Add row</Button>
              <div className="flex gap-2 pt-1">
                <Button size="sm" className="bg-lime-400 hover:bg-lime-300 text-slate-900 font-semibold flex-1" onClick={saveMins} disabled={saving} data-testid={`min-save-${program.id}`}>{saving ? "Saving…" : "Save minimums"}</Button>
                <Button size="sm" variant="ghost" onClick={() => { setMins((program.hour_minimums || []).map((m) => ({ ...m }))); setEditing(false); }}>Cancel</Button>
              </div>
            </div>
          )}
        </div>

        {/* Cost estimate */}
        <div className="rounded-lg bg-white border border-slate-200 p-3" data-testid={`cost-estimate-${program.id}`}>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><DollarSign className="h-3.5 w-3.5" /> Cost estimate</Label>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase text-slate-400">Aircraft{wantType ? ` (${wantType})` : ""}</Label>
              <Select value={acId} onValueChange={setAcId}>
                <SelectTrigger className="h-8 text-sm mt-1" data-testid={`cost-aircraft-${program.id}`}><SelectValue placeholder={aircraft.length ? "Select" : `No ${wantType || "aircraft"} assets`} /></SelectTrigger>
                <SelectContent>{aircraft.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-400">Rate</Label>
              <Select value={rateType} onValueChange={setRateType}>
                <SelectTrigger className="h-8 text-sm mt-1" data-testid={`cost-ratetype-${program.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{RATE_TYPES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase text-slate-400">Instructor (optional)</Label>
              <Select value={instrId} onValueChange={setInstrId}>
                <SelectTrigger className="h-8 text-sm mt-1" data-testid={`cost-instructor-${program.id}`}><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {instructors.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}{i.hire_rate ? ` — $${i.hire_rate}/h` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3 space-y-1 text-sm border-t border-slate-100 pt-2">
            <div className="flex justify-between text-slate-500"><span>Aircraft {ac ? `($${acRate}/h)` : ""}</span><span>{acRate ? `$${acRate}/h` : "—"}</span></div>
            {instr && <div className="flex justify-between text-slate-500"><span>Instructor</span><span>${instrRate}/h</span></div>}
            <div className="flex justify-between text-slate-500"><span>Logged</span><span>{logged}h × ${hourly}/h</span></div>
            <div className="flex justify-between font-bold text-slate-900 text-base pt-1"><span>Est. spent</span><span data-testid={`cost-spent-${program.id}`}>${estimate.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></div>
            {totalMin && <div className="flex justify-between text-xs text-slate-400"><span>Projected at {totalMin.hours}h min</span><span data-testid={`cost-projected-${program.id}`}>${projected.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></div>}
          </div>
          {wantType && aircraft.length === 0 && <p className="mt-2 text-[11px] text-amber-600">No {wantType} assets found. Add an asset with type “{wantType}” to estimate cost for this {program.discipline} syllabus.</p>}
          {aircraft.length > 0 && !hourly && <p className="mt-2 text-[11px] text-amber-600">Set hire rates on assets/users to estimate cost.</p>}
        </div>
      </div>
    </div>
  );
}
